import React, { useEffect, useRef } from 'react';
import { every, isNil, noop } from 'lodash';
import Downshift from 'downshift';
import { Amenity, CompletionType, IAutocompleteEntry } from 'utils/entities';
import { TextField } from 'ds/components/input/TextField';
import SearchIcon from 'assets/svg/addresses-autocomplete-input-search.svg';
import CityTypeIcon from 'assets/svg/city-search-type.svg';
import HoodTypeIcon from 'assets/svg/hood-search-type.svg';
import StreetTypeIcon from 'assets/svg/street-search-type.svg';
import AgentTypeIcon from 'assets/svg/agent-search-type.svg';
import DeveloperTypeIcon from 'assets/svg/developer-search-type.svg';
import ProjectTypeIcon from 'assets/svg/project-search-type.svg';
import SchoolTypeIcon from 'assets/svg/schools-search-type.svg';
import MortgageTypeIcon from 'assets/svg/mortgage-search-type.svg';
import AddressTypeIcon from 'assets/svg/address-search-type.svg';
import AreaTypeIcon from 'assets/svg/area-search-type.svg';
import { OverlayWrapper } from 'components/overlay-wrapper';
import { useScreenBreakpoint } from 'consts/breakpoints';
import CrossIcon from 'assets/svg/close.svg';
import { useLocale } from 'locale';
import {
  AutocompleteInputWrapper,
  ClearButton,
  CloseButton,
  TextFieldWrapper,
  AddressSuggestionsWrapper,
  MobileSuggestionsWrapper,
  InputWrapperStyled,
  SuggestionMessageWrapper,
  ListWrapper,
  IconWrapper,
  SearchElWrapper,
  InfoElWrapper,
  TextWrapper,
  SmallTextWrapper,
  SmallInfoWrapper,
  Line,
} from './styled';
import { STICKY_ROOT } from 'consts/rootNodes';
import { SingleInputProps } from '../types';
import { Suggestions } from '../suggestions/Suggestions';
import { Route, useRoute } from 'config/routes';
import { flattenInitialSuggestions } from '../suggestions/utils';
import { setMobileDiscoveryDisabled, setMobileDiscoveryEnabled } from 'store/state/app/actions';
import { connect } from 'react-redux';
import { serachHistoryWithoutCountrySelector } from 'store/state/domainData/selectors';
import { State } from 'store/state';
import { SearchStringValue } from '../suggestions/styled';
import { navigateTo } from 'store/state/router/actions';
import { ISaveSearch } from 'store/sagas/apiService/types';
import { config } from 'components/filters/config';

const EMPTY_ARRAY: IAutocompleteEntry[] = [];

const DEFAULT_INPUT_ICON = <SearchIcon width={24} height={24} />;
const DEFAULT_OPTIONS_COUNT = 10;

const competinTypeToIcon: Partial<Record<CompletionType | string, JSX.Element>> = {
  [CompletionType.Country]: <AddressTypeIcon />,
  [CompletionType.Address]: <AddressTypeIcon />,
  [CompletionType.CustomZone]: <AreaTypeIcon />,
  [CompletionType.City]: <CityTypeIcon />,
  [CompletionType.Neighbourhood]: <HoodTypeIcon />,
  [CompletionType.Street]: <StreetTypeIcon />,
  [CompletionType.Agent]: <AgentTypeIcon />,
  [CompletionType.Office]: <AgentTypeIcon />,
  [CompletionType.Developer]: <DeveloperTypeIcon />,
  [CompletionType.Project]: <ProjectTypeIcon />,
  [CompletionType.CommercialProject]: <ProjectTypeIcon />,
  [CompletionType.SchoolDoc]: <SchoolTypeIcon />,
  [CompletionType.Api3MortgageOffice]: <MortgageTypeIcon />,
  school: <SchoolTypeIcon />,
};

const SingleLocationAutocompleteBase: React.FunctionComponent<SingleInputProps> = (props) => {
  const {
    onSearchStrChange,
    searchStr,
    disabled,
    showType,
    onChange,
    initialValue,
    placeholder,
    showClearButton,
    autoFocus,
    initialSuggestions,
    loading,
    source,
    inlineView,
    hasError,
    errorMessage,
    sendOnClickEvent = noop,
    sendOnHistoryClickEvent = noop,
    onBlur = noop,
    onSuggest = noop,
    optionsCount = DEFAULT_OPTIONS_COUNT,
    suggestedAddresses = EMPTY_ARRAY,
    inputIcon = DEFAULT_INPUT_ICON,
    overlayId = STICKY_ROOT,
    downshiftRef,
    onDownshiftStateChange,
    suggestionItemIcon,
    suggestionMessage,
    makeMobileDiscoveryDisabled,
    makeMobileDiscoveryEnabled,
    noResult,
    suggestionFooter,
    suggestionsWithTitle,
    useSearchHistory,
    searchHistory,
    navigate,
  } = props;

  const { route } = useRoute();
  const { t, formatMoneyWithMetricPrefix } = useLocale();
  const inputRef = useRef<HTMLDivElement>(null);
  const currentRange = useScreenBreakpoint();
  const isMobileView = inlineView ? false : currentRange <= 2;

  useEffect(() => {
    if (initialValue) {
      onSelectedAddressChange(initialValue as IAutocompleteEntry);
    }
  }, []);

  useEffect(() => {
    onSuggest(suggestedAddresses);
  }, [ suggestedAddresses ]);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSearchStrChange(e.currentTarget.value);
    onChange(null);
  };

  const handleClear = () => {
    onSearchStrChange('');
    onChange(null);
    const inputEl = inputRef.current.querySelector('input');
    inputEl.focus();
  };

  const flatInitialSuggestions = flattenInitialSuggestions(initialSuggestions);
  const allSuggestedAddresses = [ ...suggestedAddresses, ...flatInitialSuggestions ];

  const onSelectedAddressChange = (selectedAddress: IAutocompleteEntry) => {
    if (selectedAddress) {
      onSearchStrChange(selectedAddress.name);
      onChange(
        selectedAddress,
        allSuggestedAddresses.indexOf(selectedAddress),
        suggestedAddresses.length || flatInitialSuggestions.length
      );
    }
    else {
      onSearchStrChange(initialValue ? initialValue.name : '');
    }
  };

  const handleInputChange = (selectedItemDocId: string) => {
    onSelectedAddressChange(allSuggestedAddresses.find(address => address.docId === selectedItemDocId));
    inputRef.current.querySelector('input').blur();
  };

  const handleSearchHistoryNavigate = (s: ISaveSearch) => {
    const query = s.query;
    const { __typename, ...amenities } = query.amenities as Partial<Record<Amenity, boolean>> & { __typename: string };
    const amenityArr = Object.keys(amenities).map(el => (query.amenities as any)[el] && el).filter(Boolean) as any;
    const areaRange = every(query.areaRange, isNil) ? undefined : query.areaRange;
    const bathsRange = every(query.bathsRange, isNil) ? undefined : query.bathsRange;
    const buildingClass = !query.buildingClass.length ? undefined : query.buildingClass;
    const floorRange = every(query.floorRange, isNil) ? undefined : query.floorRange;
    const generalCondition = !query.generalCondition.length ? undefined : query.generalCondition;
    const monthlyTaxRange = every(query.monthlyTaxRange, isNil) ? undefined : query.monthlyTaxRange;
    const ppmRange = every(query.ppmRange, isNil) ? undefined : query.ppmRange;
    const priceRange = every(query.priceRange, isNil) ? undefined : query.priceRange;
    const roomsRange = every(query.roomsRange, isNil) ? undefined : query.roomsRange;
    const sellerType = !query.sellerType.length ? undefined : query.sellerType;

    const noFilters = !amenityArr.length
     && !areaRange
     && !bathsRange
     && !buildingClass
     && !floorRange
     && !generalCondition
     && !monthlyTaxRange
     && !ppmRange
     && !priceRange
     && !roomsRange
     && !sellerType;

    sendOnHistoryClickEvent();

    navigate(Route.Search, {
      term: [ s.term ],
      dealType: s.query.dealType,
      filters: noFilters ? undefined : {
        amenities: amenityArr.length ? amenityArr : undefined,
        areaRange,
        bathsRange,
        propertyTypes: buildingClass,
        floorRange,
        conditions: generalCondition,
        monthlyTaxRange,
        ppmRange,
        priceRange: priceRange ? [ priceRange[0] || 0, priceRange[1] || config.residential.budgetByDealType[s.query.dealType].budgetSliderProps.max ] : priceRange,
        roomsRange,
        seller: sellerType,
      },
    });
  };

  return (
    <AutocompleteInputWrapper data-auto="address-autocomplete-input-wrapper">
      <Downshift
        ref={downshiftRef as any}
        defaultHighlightedIndex={0}
        initialIsOpen={autoFocus}
        onChange={handleInputChange}
        onStateChange={onDownshiftStateChange}
      >
        {({ getInputProps, getItemProps, getRootProps, isOpen, toggleMenu, openMenu, highlightedIndex }) => {
          const isMobileViewOpen = isOpen && isMobileView;

          if (isMobileViewOpen) {
            makeMobileDiscoveryDisabled();
          }

          // We need to send click event in focus handler, because sometimes
          // we use autofocus on mobile instead of direct click
          const handleTextFieldFocus = () => {
            sendOnClickEvent(source, route.name);
          };

          const handleClick = () => !isOpen && !searchStr ? toggleMenu() : openMenu();

          const handleClose = () => {
            toggleMenu();
            onSearchStrChange(initialValue ? initialValue.name : '');
            makeMobileDiscoveryEnabled();
            onBlur();
          };

          const input = (
            <TextField
              {...getInputProps()}
              data-auto="autocomplete-textfield"
              disabled={disabled}
              onClick={handleClick}
              icon={isMobileViewOpen || useSearchHistory ? null : inputIcon}
              hasError={hasError}
              errorMessage={errorMessage}
              value={searchStr}
              onChange={onInputChange}
              placeholder={placeholder}
              fullWidth
              onBlur={onBlur}
              onFocus={handleTextFieldFocus}
              autoFocus={isOpen || autoFocus}
            />
          );

          const clearButton = (
            <ClearButton data-auto="search-clear" onClick={handleClear}>
              <CrossIcon width={16} height={16} />
            </ClearButton>
          );

          const closeButton = (
            <CloseButton
              data-auto="close-button"
              onClick={handleClose}
              weight="medium"
            >
              {t('autocompleteInput.cancel')}
            </CloseButton>
          );

          const list = useSearchHistory ? (
            <>
              {(isOpen || isMobileViewOpen) && !loading && searchStr && suggestedAddresses ? (
                <ListWrapper data-auto="autocomplete-suggestions">{suggestedAddresses.length ? suggestedAddresses.slice(0, optionsCount).map((el, index) => (
                  <SearchElWrapper
                    {...getItemProps({
                      key: el.docId,
                      item: el.docId,
                      index,
                    })}
                  >
                    <IconWrapper>{competinTypeToIcon[el.type]}</IconWrapper>
                    <InfoElWrapper data-auto="autocomplete-suggestion">
                      <TextWrapper data-auto="suggetion-text">{el.name}</TextWrapper>
                      <SmallTextWrapper data-auto="suggestion-type">{t('completionType.seachInput', { type: el.type })}</SmallTextWrapper>
                    </InfoElWrapper>
                  </SearchElWrapper>
                )) : <div>{t('autocompleteInput.noResult.message')}<SearchStringValue weight="bold"> {searchStr}</SearchStringValue></div>}</ListWrapper>
              ) : null}
              {(isOpen || isMobileViewOpen) && !loading && !searchStr && searchHistory && searchHistory.length ? (
                <ListWrapper>{searchHistory.slice(0, optionsCount).map((el, index) => {
                  const [ roomMin, roomMax ] = el.query.roomsRange;
                  const roomLabel = t('filters.bedrooms');
                  const withPlus = (v: number) => `${v}${v === 6 ? '+' : ''}`;

                  let bedsStr;

                  if (roomMin && roomMax && roomMin !== roomMax) {
                    bedsStr = `${roomMin}-${roomMax} ${roomLabel}`;
                  }
                  else {
                    const value = roomMin || roomMax;
                    bedsStr = `${withPlus(value)} ${roomLabel}`;
                  }

                  const [ priceMin, priceMax ] = el.query.priceRange;
                  let priceStr;

                  if (priceMin && priceMax) {
                    priceStr = `${formatMoneyWithMetricPrefix(priceMin)}-${formatMoneyWithMetricPrefix(priceMax)}`;
                  }
                  else {
                    if (priceMax) {
                      priceStr = t('lastSearch.type.element.price.max', { count: formatMoneyWithMetricPrefix(priceMax) });
                    }
                    else if (priceMin) {
                      priceStr = t('lastSearch.type.element.price.min', { count: formatMoneyWithMetricPrefix(priceMin) });
                    }
                  }
                  return (
                    <SearchElWrapper onClick={() => handleSearchHistoryNavigate(el)} key={`${el.term}_${index}`}>
                      {el.searchType ? <IconWrapper>{competinTypeToIcon[el.searchType.toLocaleLowerCase() as CompletionType]}</IconWrapper> : null}
                      <InfoElWrapper>
                        <TextWrapper>{el.title}</TextWrapper>
                        <SmallInfoWrapper>
                          {el.searchType ? <SmallTextWrapper>{t('completionType.seachInput', { type: el.searchType.toLocaleLowerCase() })}</SmallTextWrapper> : null}
                          {el.query.dealType ? (
                            <>
                              <Line />
                              <SmallTextWrapper>{t('filters.dealType', { dealType: el.query.dealType })}</SmallTextWrapper>
                            </>
                          ) : null}
                          {el.query.buildingClass && el.query.buildingClass.length ? (
                            <>
                              <Line />
                              <SmallTextWrapper>{t('agent.office.propertyType', { propertyType: el.query.buildingClass[0] })}</SmallTextWrapper>
                            </>
                          ) : null}
                          {el.query.priceRange && (el.query.priceRange[0] || el.query.priceRange[1]) ? (
                            <>
                              <Line />
                              <SmallTextWrapper data-auto="homepage-last-search-carousel-price">{priceStr}</SmallTextWrapper>
                            </>
                          ) : null}
                          {el.query.roomsRange && (el.query.roomsRange[0] || el.query.roomsRange[1]) ? (
                            <>
                              <Line />
                              <SmallTextWrapper data-auto="homepage-last-search-carousel-rooms">{bedsStr}</SmallTextWrapper>
                            </>
                          ) : null}
                        </SmallInfoWrapper>
                      </InfoElWrapper>
                    </SearchElWrapper>
                  );
                })}</ListWrapper>
              ) : null}
            </>
          ) : (
            <Suggestions
              isOpen={isOpen}
              initialSuggestions={initialSuggestions}
              suggestedAddresses={suggestedAddresses}
              loading={loading}
              searchStr={searchStr}
              source={source}
              optionsCount={optionsCount}
              showType={showType}
              highlightedIndex={highlightedIndex}
              itemPropsGetter={getItemProps}
              itemIcon={suggestionItemIcon}
              noResult={noResult}
              suggestionFooter={suggestionFooter}
              suggestionsWithTitle={suggestionsWithTitle}
            >
              {(suggestions) => (
                <AddressSuggestionsWrapper isMobile={isMobileView} data-auto="autocomplete-suggestions" className="autocomplete-suggestions">
                  {suggestionMessage && suggestedAddresses.length ? <SuggestionMessageWrapper>{suggestionMessage}</SuggestionMessageWrapper> : null}
                  {suggestions}
                </AddressSuggestionsWrapper>
              )}
            </Suggestions>
          );

          const content = (
            <div>
              <InputWrapperStyled>
                <TextFieldWrapper
                  className="autocomplete-textfield-wrapper"
                  ref={inputRef}
                  isMobile={isMobileView}
                  isOpen={isOpen}
                  showClearButton={showClearButton}
                >
                  {input}
                  {showClearButton && searchStr && isOpen ? clearButton : null}
                </TextFieldWrapper>

                {isMobileViewOpen ? closeButton : null}

              </InputWrapperStyled>

              {list}
            </div>
          );

          return isMobileViewOpen ? (
            <OverlayWrapper portalId={overlayId} {...getRootProps({ refKey: 'innerRef' })}>
              <MobileSuggestionsWrapper>{content}</MobileSuggestionsWrapper>
            </OverlayWrapper>
          ) : content;
        }}
      </Downshift>
    </AutocompleteInputWrapper>
  );
};

const mapStateToProps = (state: State) => ({
  searchHistory: serachHistoryWithoutCountrySelector(state),
});

const mapDispatchToProps = {
  makeMobileDiscoveryDisabled: setMobileDiscoveryDisabled,
  makeMobileDiscoveryEnabled: setMobileDiscoveryEnabled,
  navigate: navigateTo,
};

export const SingleLocationAutocomplete = connect(mapStateToProps, mapDispatchToProps)(SingleLocationAutocompleteBase);
